// src/components/circles/circleDisplay.ts
//
// Presentation helpers for CRM Circles. Type labels + palette + icons.
// Kept as a pure data module so tests can import it without a DOM.

import type { CircleType, CircleVisibility } from '../../types/api';

// TEAM-UX-1 — display copy only; the CircleType keys, `centralTeamId`,
// and every membership behaviour are unchanged.
//
// `team` renders as "Central Team" because a Circle of this type is
// backed by a Central Team (see CENTRAL_TEAM_BACKED_CIRCLE_TYPES below).
// A bare "Team" badge collided with the old "Team" nav item and read as
// if the Circle were the CRM roster page, which it is not.
export const CIRCLE_TYPE_LABELS: Record<CircleType, string> = {
  people_group: 'People Group',
  custom: 'Custom',
  department: 'Department',
  team: 'Central Team',
  smart_segment: 'Smart Segment',
  territory: 'Territory',
  account_team: 'Account Team',
};

/**
 * Distinct Tailwind chip palette per Circle type. Kept static so users
 * learn to recognize types by color across the app.
 */
export const CIRCLE_TYPE_CHIP: Record<CircleType, string> = {
  people_group: 'bg-purple-100 text-purple-700',
  custom: 'bg-gray-100 text-gray-700',
  department: 'bg-emerald-100 text-emerald-700',
  team: 'bg-blue-100 text-blue-700',
  smart_segment: 'bg-amber-100 text-amber-700',
  territory: 'bg-rose-100 text-rose-700',
  account_team: 'bg-indigo-100 text-indigo-700',
};

export const CIRCLE_VISIBILITY_LABELS: Record<CircleVisibility, string> = {
  private: 'Private',
  workspace: 'Workspace',
  restricted: 'Restricted',
};

export const CIRCLE_VISIBILITY_CHIP: Record<CircleVisibility, string> = {
  private: 'bg-gray-100 text-gray-700',
  workspace: 'bg-sky-100 text-sky-700',
  restricted: 'bg-orange-100 text-orange-700',
};

/**
 * Categories the list UI groups Circle types into. Backend supports the
 * full CircleType enum; the UI clusters them for filtering.
 */
export interface CircleTypeGroup {
  key: string;
  label: string;
  types: CircleType[];
}

export const CIRCLE_TYPE_GROUPS: CircleTypeGroup[] = [
  { key: 'people', label: 'People', types: ['people_group', 'custom'] },
  { key: 'org', label: 'Org', types: ['department', 'team'] },
  {
    key: 'segments',
    label: 'Segments',
    types: ['smart_segment', 'territory', 'account_team'],
  },
];

// Circle types that may reference a Central Team primitive. Central Team
// is the organizational source of truth; only Circle types that represent
// org units (`department`, `team`) can be linked. Account Team is a
// CRM-only construct (the internal team responsible for a customer
// account) and does NOT mirror a Central Team.
export const CENTRAL_TEAM_BACKED_CIRCLE_TYPES: ReadonlySet<CircleType> = new Set<CircleType>([
  'department',
  'team',
]);

export function isCentralTeamBackedCircleType(type: CircleType): boolean {
  return CENTRAL_TEAM_BACKED_CIRCLE_TYPES.has(type);
}

/**
 * Whether a Circle's USER members can be resolved outside a user request —
 * the requirement for routing calls to it (telephony) or using it as a Work
 * Accountability audience. Mirrors the server's
 * `userMembershipUnresolvableReason` (services/circleMembershipResolver):
 * Smart Circles are contact-based (no users), and Department/Team Circles'
 * membership is the Central Team roster, which the server can only read with
 * a user token. The server refuses both; the pickers do not offer them.
 */
export function circleHasResolvableUserMembers(type: CircleType): boolean {
  return type !== 'smart_segment' && !CENTRAL_TEAM_BACKED_CIRCLE_TYPES.has(type);
}
